import type { CollectStatus, GatewaySchedule } from '../api/types'
import { formatCount } from './format'
import { formatTime } from './time'

/** Round minutes as people write them: ":00, :30". */
export function formatMinutes(minutes: number[]): string {
  return minutes.map((m) => `:${String(m).padStart(2, '0')}`).join(', ')
}

/** What a person typed into the minutes field: ":00, :30", "0 30" or blank
 *  (blank goes back to the learned minutes). The server checks again. */
export function parseMinutes(text: string): { minutes: number[] | null } | { error: string } {
  const parts = text.split(/[\s,;]+/).map((p) => p.replace(/^:/, '')).filter(Boolean)
  if (parts.length === 0) return { minutes: null }
  if (parts.some((p) => !/^\d{1,2}$/.test(p))) {
    return { error: 'Write minutes past the hour, like :00, :30.' }
  }
  const minutes = [...new Set(parts.map(Number))].sort((a, b) => a - b)
  if (minutes.some((m) => m > 59)) return { error: 'Minutes past the hour run from 0 to 59.' }
  if (minutes.length > 6) return { error: 'Give one to six minutes past the hour.' }
  return { minutes }
}

/** The line under the Collect now button: what the latest request did. */
export function collectLine(status: CollectStatus): string {
  const latest = status.latest
  if (!latest) return ''
  const at = latest.finished_at ? formatTime(latest.finished_at) : ''
  switch (latest.status) {
    case 'pending':
      return `Waiting to start, asked by ${latest.requested_by} at ${formatTime(latest.requested_at)}.`
    case 'running':
      return 'Collecting…'
    case 'failed':
      return `Could not collect at ${at}: ${latest.error ?? 'no reason given'}`
    case 'empty':
      return `Nothing new at ${at}: everything the station holds was already stored.`
    default: {
      const n = latest.rows_written ?? 0
      const partly = latest.status === 'partial' ? ' Some files could not be read.' : ''
      return `Collected ${formatCount(n)} ${n === 1 ? 'reading' : 'readings'} at ${at}, ` +
        `asked by ${latest.requested_by}.${partly}`
    }
  }
}

/** The note above the button when the health checks already have the
 *  station's source failing: a request may still go through (a gateway just
 *  fixed), so the button stays, but nobody should wait on it unwarned. */
export function sourceNote(status: CollectStatus): string {
  if (!status.source_down_since) return ''
  const who = status.source === 'MDS' ? 'MDS has' : 'The gateway has'
  return `${who} not been answering since ${formatTime(status.source_down_since)}. ` +
    'A request now says within about a minute whether it got through.'
}

/** The admin row's summary of the last day's rounds. */
export function roundLine(day: GatewaySchedule['last_day']): string {
  if (!day || day.rounds === 0) return 'No rounds yet'
  const avg = day.reads_avg === null ? '' : ` (${day.reads_avg} a round)`
  return `${formatCount(day.rounds)} rounds, ${formatCount(day.complete)} complete, ` +
    `${formatCount(day.gave_up)} ended early; ${formatCount(day.reads)} status reads${avg}`
}
